import { useReducer } from 'react';
import { FormField, TextInput, Select } from 'grommet';
import { Details, File, FormMarkup } from '@components/basicComponents';
import { getSortedFormFields, handleFormSubmit } from '@utils/forms';
import type { FormExtendedEvent } from 'grommet/components/Form';
import type { FormProps } from '@customTypes/types';
import type { LoginSupportFormState } from '@customTypes/stateTypes';
import { helpWithOptions } from './LoginSupportFormOptions';
import { ChinaRegions, Languages } from '@customTypes/enums';

const formTitle: string = 'Sign In Support';

/* Alphabetical order */
// const supportLanguages = [Languages.EN, Languages.ZH_CN, Languages.ZH_TW, Languages.JA, Languages.KO];
const supportLanguages = [Languages.EN, Languages.JA];

export const LoginSupportForm = (props: FormProps): JSX.Element => {
  const [state, updateState] = useReducer(
    (prev: LoginSupportFormState, newState: Partial<LoginSupportFormState>) => ({
      ...prev,
      ...newState,
    }),
    {
      helpWith: '',
      username: false,
      showDetails: false,
      numFiles: 0,
    }
  );

  const onFileChange = (value: number): void => {
    updateState({ numFiles: value });
  };

  const onSubmit = ({ target,value }: FormExtendedEvent): void => {
    let namespace;
    let subject;

    switch (value.preferredLanguage) {
      case Languages.EN:
        namespace = 'hpe_loginsupport';
        subject = `IAM : ${state.helpWith} - {formId}`;
        break;
      case Languages.ZH_TW:
        if (value.countryOrRegion === ChinaRegions.HK) {
          namespace = 'hpe_loginsupport_zh_hk';
        } else if (value.countryOrRegion === ChinaRegions.TW) {
          namespace = 'hpe_loginsupport_zh_tw';
        }

        const regionISO = Object.keys(ChinaRegions).find(key => ChinaRegions[key] === value.countryOrRegion);
        subject = `IAM : Chinese : ${regionISO} : ${state.helpWith} - {formId}`;
        break;
      default:
        const languageISO = Object.keys(Languages).find(key => Languages[key] === value.preferredLanguage)?.toLowerCase();
        namespace = 'hpe_loginsupport_' + languageISO;
        subject = `IAM : ${value.preferredLanguage} : ${state.helpWith} - {formId}`;
    }

    const data = getSortedFormFields(target, namespace, subject);

    handleFormSubmit(data, props.onSuccess, props.onError, props.onLoading);
  };

  const onReset = (): void => {
    clearFirstSelectState();
    updateState({ helpWith: '', numFiles: 0 });
  };

  const helpWithChange = (option: string): void => {
    if (option === state.helpWith) {
      return;
    }

    clearFirstSelectState();
    updateState({ helpWith: option, username: option !== 'Other', showDetails: true });
  };

  const clearFirstSelectState = (): void => {
    updateState({ username: false, showDetails: false });
  };

  return (
    <FormMarkup
      onSubmit={onSubmit}
      onReset={onReset}
      formTitle={formTitle}
      translate
      doubleFieldPhone
      supportLanguages={supportLanguages}
      supportHoursAcknowledgement
      formFields={
        <>
          <FormField name="helpWith" htmlFor="help-with" label="What do you need help with?" required>
            <Select
              id="help-with"
              name="helpWith"
              placeholder="Select"
              options={Object.values(helpWithOptions)}
              onChange={({ option }) => helpWithChange(option)}
            />
          </FormField>

          {state.username &&
            <FormField name="username" htmlFor="username" label="Username" required>
              <TextInput id="username" name="username" placeholder="Username" />
            </FormField>
          }

          {state.showDetails &&
            <>
              <Details />

              <FormField name="application" htmlFor="application-url" label="Application URL" required>
                <TextInput id="application-url" name="application" placeholder="Application URL (Ex : https://support.hpe.com/)" />
              </FormField>

              <File numFiles={state.numFiles} onFileChange={onFileChange} />
            </>
          }
        </>
      }
    />
  );
};
